import React, { useEffect, useReducer, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { ROLES, USER_ENGAGEMENT_PAGE_KEYS } from '@silverfort-nodelibs/common/utils/consts';

import { deleteFromSettingsByIndex } from '../settingsSlice';
import useSearch from '../../hooks/useSearch';
import { useTabs } from '../../hooks/useTabs';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import { CATEGORIES } from '../../common/categories';
import { fieldsReducer, initFieldsState, mapToSettings } from './customInsightsReducer';
import * as actions from './customInsightsActions';

import Category from '../../components/Category/Category';
import CustomInsight from '../../components/CustomInsight/CustomInsight';
import Button from '../../components/Controls/Button/Button';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';
import { httpPost, isUserPermitted } from '../../../utils';
import { RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS } from '@silverfort-nodelibs/common/utils/insight';
import CustomInsightCreationModal from '../../../Insights/CustomInsightCreationModal';

const { CUSTOM_INSIGHTS } = CATEGORIES;
const SERVICE_ACCOUNT_CUSTOM_INSIGHT_ID = 'SERVICE_ACCOUNT';
const CustomInsights = ({ saveFunc, disableSave }) => {
    const history = useHistory();
    const storeDispatch = useDispatch();

    const user = useSelector((state) => state.userData.user);
    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);

    const [selectNewCustomInsightTab, setSelectCustomInsightTab] = useState(false);
    const [deletedCustomInsightId, setDeletedCustomInsightId] = useState(null);
    const [newCustomInsightId, setNewCustomInsightId] = useState(null);
    const [showCreationModal, setShowCreationModal] = useState(false);
    const [staticInsightsDropdownOptions, setStaticInsightsDropdownOptions] = useState([]);

    const settings = useSelector((state) => state.settings.settings);
    const searchQuery = useSelector((state) => state.settings.searchQuery);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const visibleFields = useSearch(
        Object.fromEntries(Object.entries(fields).filter(([key]) => key !== SERVICE_ACCOUNT_CUSTOM_INSIGHT_ID))
    );
    const [selectedTab, setSelectedTab] = useTabs(CUSTOM_INSIGHTS, visibleFields);
    const [save, discard] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedTab, dispatch);

    useEffect(() => {
        let newId = null;
        for (const insightId of Object.keys(visibleFields)) {
            if (!settings.StaticCustomInsights.find((ins) => ins.id === insightId)) {
                newId = insightId;
                break;
            }
        }
        setNewCustomInsightId(newId);
    }, [visibleFields, settings]);

    useEffect(() => {
        if (newCustomInsightId && selectNewCustomInsightTab) {
            history.push(`/Settings/${CUSTOM_INSIGHTS.NAME}#${newCustomInsightId}`);
            setSelectedTab(newCustomInsightId);
            setSelectCustomInsightTab(false);
        }
    }, [selectNewCustomInsightTab, newCustomInsightId, history, setSelectedTab]);

    useEffect(() => {
        httpPost('/data/userEngagement', { pageId: USER_ENGAGEMENT_PAGE_KEYS.SETTINGS_CUSTOM_INSIGHTS });
    }, []);

    const addNewCustomInsight = (insightId) => {
        const insightName = insightId ? RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS[insightId]?.NAME : null;
        const entityType = RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS[insightId].ENTITY_TYPE;

        dispatch({
            type: actions.ADD_CUSTOM_INSIGHT,
            payload: { customInsightId: insightId, insightName, entityType }
        });
        setSelectCustomInsightTab(true);
    };

    const deleteCustomInsight = async () => {
        if (deletedCustomInsightId !== newCustomInsightId) {
            try {
                const { data } = await httpPost(`/Settings/deleteCustomInsight`, {
                    customInsightId: deletedCustomInsightId
                });
                if (!data.result) {
                    console.error(`Failed to delete custom insight id: ${deletedCustomInsightId}`);
                    // TODO: show error
                }
            } catch (err) {
                console.error(`Failed to delete custom insight id: ${deletedCustomInsightId}`);
                // TODO: show error
            }
        }
        setDeletedCustomInsightId(null);
        dispatch({
            type: actions.DELETE_CUSTOM_INSIGHT,
            payload: { customInsightId: deletedCustomInsightId }
        });

        await saveFunc({
            UserCustomInsights: settings.UserCustomInsights,
            StaticCustomInsights: settings.StaticCustomInsights.filter(
                (insight) => insight.id !== deletedCustomInsightId
            )
        });
    };

    const onDeleteCustomInsightClicked = (customInsightId) => {
        setDeletedCustomInsightId(customInsightId);
    };

    const onDiscardClicked = () => {
        if (selectedTab === newCustomInsightId) {
            dispatch({
                type: actions.DISCARD_NEW_CUSTOM_INSIGHT,
                payload: { customInsightId: newCustomInsightId }
            });
        } else {
            discard(false);
        }
    };

    const onAddInsightClicked = () => {
        const existingStaticInsightIDs = Object.keys(visibleFields).filter((id) => !id.endsWith('custom_rule'));
        const dropdownOptions = Object.values(RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS)
            .filter(
                (field) =>
                    !field.IS_POSTURE_INSIGHT &&
                    !existingStaticInsightIDs.includes(field.ID) &&
                    field.ID !== SERVICE_ACCOUNT_CUSTOM_INSIGHT_ID
            )
            .sort((indicator1, indicator2) => {
                if (indicator1.ENABLE_INCLUSION) {
                    return -1;
                }
                return indicator1.NAME.localeCompare(indicator2.NAME);
            })
            .map((field) => {
                return {
                    value: field.ID,
                    label: field.NAME
                };
            });
        setStaticInsightsDropdownOptions(dropdownOptions);
        setShowCreationModal(true);
    };

    const hideCreationModal = () => {
        setShowCreationModal(false);
    };

    let content = null;
    for (const tabKey of Object.keys(visibleFields)) {
        if (selectedTab === tabKey) {
            content = (
                <CustomInsight
                    fields={visibleFields[tabKey].fields}
                    tabKey={tabKey}
                    dispatch={dispatch}
                    onDelete={onDeleteCustomInsightClicked}
                    allowDelete={isEditor}
                />
            );
            break;
        }
    }

    if (!content) {
        content = <div></div>;
    }

    const addCustomInsight = (
        <Button disabled={newCustomInsightId || searchQuery} onClick={onAddInsightClicked}>
            + Customize Insight
        </Button>
    );

    return (
        <>
            <Category
                category={CUSTOM_INSIGHTS}
                fields={fields}
                visibleFields={visibleFields}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                tabButton={addCustomInsight}
                saveSettings={save}
                discard={onDiscardClicked}
                discardAll={() => discard(true)}
                disableSave={disableSave}>
                {content}
            </Category>
            {deletedCustomInsightId && (
                <ConfirmationMessage
                    title={'Delete Custom Insight'}
                    body={`Are you sure you want to delete "${visibleFields[deletedCustomInsightId].name}" ?`}
                    approveText={'Delete'}
                    cancelText={'Cancel'}
                    approveStyle={'danger'}
                    OnCancel={() => setDeletedCustomInsightId(null)}
                    OnApprove={deleteCustomInsight}
                />
            )}

            {showCreationModal && (
                <CustomInsightCreationModal
                    onCancel={hideCreationModal}
                    onCreate={(selectedStaticInsight) => {
                        addNewCustomInsight(selectedStaticInsight);
                        hideCreationModal();
                    }}
                    staticInsightsDropdownOptions={staticInsightsDropdownOptions}
                />
            )}
        </>
    );
};

export default CustomInsights;
